import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Check, ChevronDown, Keyboard, KeyRound, LogOut, Menu, Moon, Sun } from 'lucide-react'
import { BrandMark, Sidebar, isRouteActive } from './Sidebar'
import type { DrawerItem } from '@/utils/routes'
import { clearAuthStorage, getLoginUserEmail } from '@/lib/auth'
import { useKeyboardShortcuts, APP_SHORTCUTS } from '@/hooks/useKeyboardShortcuts'
import { useDensity, type Density } from '@/hooks/useDensity'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { TooltipProvider } from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'

const SIDEBAR_STORAGE_KEY = 'sidebar-collapsed'

interface AppShellProps {
  title: string
  navItems: DrawerItem[]
  children: ReactNode
}

/** Derive a human-readable page title from the current pathname. */
function usePageTitle(navItems: DrawerItem[]): string {
  const { pathname } = useLocation()

  function findTitle(items: DrawerItem[]): string | undefined {
    for (const item of items) {
      if (isRouteActive(pathname, item.route)) return item.text
      if (item.children?.length) {
        const child = findTitle(item.children)
        if (child) return child
      }
    }
    return undefined
  }

  return findTitle(navItems) ?? 'Operations'
}

/** Get initials from an email address, e.g. john.doe@example.com -> JD */
function emailInitials(email: string): string {
  const local = email.split('@')[0] ?? ''
  const parts = local.split(/[._-]/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return local.slice(0, 2).toUpperCase() || 'OP'
}

function handleLogout() {
  clearAuthStorage()
  window.location.href = `${window.location.origin}/login`
}

function ThemeToggle() {
  const [dark, setDark] = useState(() => document.documentElement.dataset.theme === 'dark')
  const toggle = () => {
    const next = !dark
    document.documentElement.dataset.theme = next ? 'dark' : 'light'
    localStorage.setItem('panel.theme', next ? 'dark' : 'light')
    setDark(next)
  }
  const label = `Switch to ${dark ? 'light' : 'dark'} theme`
  return (
    <Button variant="ghost" size="icon" onClick={toggle} title={label} aria-label={label}>
      {dark ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
    </Button>
  )
}

function MobileNavItem({ item }: { item: DrawerItem }) {
  const location = useLocation()
  const isActive = isRouteActive(location.pathname, item.route)

  return (
    <li>
      <SheetClose asChild>
        <Link
          to={item.route}
          aria-current={isActive ? 'page' : undefined}
          className={cn(
            'flex min-h-[var(--control-h-touch)] items-center gap-3 rounded-md px-3 text-base font-medium text-sidebar-text transition-colors duration-fast hover:bg-sidebar-hover',
            'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--color-sidebar-bg),0_0_0_4px_var(--color-sidebar-text)]',
            isActive && 'bg-sidebar-hover font-semibold shadow-[inset_3px_0_0_var(--color-primary)]',
          )}
        >
          {typeof item.icon !== 'string' && item.icon ? (
            <span className="inline-flex shrink-0 [&_svg]:size-5" aria-hidden="true">{item.icon}</span>
          ) : (
            <span className="inline-flex size-5 items-center justify-center font-semibold" aria-hidden="true">
              {item.text.slice(0, 2).toUpperCase()}
            </span>
          )}
          <span className="truncate">{item.text}</span>
        </Link>
      </SheetClose>
    </li>
  )
}

function MobileNavigation({
  items,
  open,
  onOpenChange,
}: {
  items: DrawerItem[]
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[288px] max-w-[86vw] border-sidebar-hover bg-sidebar p-0 text-sidebar-text">
        <SheetHeader className="h-topbar justify-center border-b border-sidebar-hover px-4 text-left">
          <SheetTitle className="flex items-center gap-3 text-sidebar-text">
            <BrandMark />
            Fulfillment Panel
          </SheetTitle>
          <SheetDescription className="sr-only">Primary navigation</SheetDescription>
        </SheetHeader>
        <nav aria-label="Primary" className="px-2 py-3">
          <ul className="flex flex-col gap-1">
            {items.map((item) => (
              <MobileNavItem key={item.id} item={item} />
            ))}
          </ul>
        </nav>
      </SheetContent>
    </Sheet>
  )
}

// ── Shortcuts dialog ──────────────────────────────────────────────────────────
function ShortcutsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="size-5 text-text-secondary" aria-hidden="true" />
            Keyboard shortcuts
          </DialogTitle>
          <DialogDescription>Shortcuts are disabled while typing in a field.</DialogDescription>
        </DialogHeader>
        <ul className="flex flex-col gap-2">
          {APP_SHORTCUTS.map((s) => (
            <li key={`${s.key}-${s.description}`} className="flex items-center justify-between gap-4">
              <span className="text-text-secondary">{s.description}</span>
              <kbd className="inline-flex items-center gap-1 rounded-sm border border-border-strong bg-surface-sunken px-2 py-0.5 font-mono font-medium text-text">
                {s.shift ? <span>Shift</span> : null}
                {s.key}
              </kbd>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  )
}

// ── Profile menu ──────────────────────────────────────────────────────────────
function ProfileMenu({
  email,
  initials,
  density,
  setDensity,
}: {
  email: string
  initials: string
  density: Density
  setDensity: (next: Density) => void
}) {

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={email ? `Account menu for ${email}` : 'Account menu'}
          className="flex h-control items-center gap-2 rounded-md px-2 hover:bg-surface-sunken focus-visible:outline-none focus-visible:shadow-focus"
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary font-semibold text-primary-on" aria-hidden="true">
            {initials}
          </span>
          {email ? <span className="hidden max-w-[140px] truncate text-text-secondary sm:block">{email}</span> : null}
          <ChevronDown className="size-4 text-text-secondary" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {email ? (
          <>
            <DropdownMenuLabel>Signed in as</DropdownMenuLabel>
            <p className="truncate px-3 pb-2 font-medium text-text">{email}</p>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuLabel>Density</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => setDensity('comfortable')}>
          Comfortable
          {density === 'comfortable' ? <Check className="ml-auto" aria-label="Selected" /> : null}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setDensity('compact')}>
          Compact
          {density === 'compact' ? <Check className="ml-auto" aria-label="Selected" /> : null}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/reset-password">
            <KeyRound aria-hidden="true" />
            Change password
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem destructive onSelect={handleLogout}>
          <LogOut aria-hidden="true" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ── AppShell ──────────────────────────────────────────────────────────────────
export function AppShell({ title, navItems, children }: AppShellProps) {
  const pageTitle = usePageTitle(navItems)
  const { pathname } = useLocation()
  const email = getLoginUserEmail()
  const initials = emailInitials(email)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const mainRef = useRef<HTMLElement>(null)
  const firstRender = useRef(true)
  const ledgerEnabled = useFeatureFlag('ui_ledger')

  // Density is applied as data-density on <html>; font sizes never change with it.
  const { density, setDensity } = useDensity()

  useEffect(() => {
    document.documentElement.dataset.ui = ledgerEnabled ? 'ledger' : 'legacy'
  }, [ledgerEnabled])

  // Sync browser tab title with current page
  useEffect(() => {
    document.title = pageTitle ? `${pageTitle} - Fulfillment Panel` : 'Fulfillment Panel'
  }, [pageTitle])

  // Move focus to <main> on route change so keyboard and screen reader users start at the content.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    mainRef.current?.focus({ preventScroll: true })
  }, [pathname])

  // Sidebar collapse - persisted; defaults to icons-only on 1024-1279px viewports
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY)
    if (stored !== null) return stored === 'true'
    return window.innerWidth < 1280
  })
  const toggleSidebar = useCallback(() => {
    setCollapsed((v) => {
      const next = !v
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next))
      return next
    })
  }, [])

  const [shortcutsOpen, setShortcutsOpen] = useState(false)

  const navRef = useRef<HTMLElement>(null)
  const focusSearch = useCallback(() => {
    const el = document.querySelector<HTMLElement>('[data-search-input]')
    el?.focus()
  }, [])

  useKeyboardShortcuts([
    { key: 'B', shift: true, description: 'Toggle sidebar', handler: toggleSidebar },
    { key: 'M', shift: true, description: 'Focus navigation menu', handler: () => {
      const firstLink = navRef.current?.querySelector<HTMLElement>('a, button')
      firstLink?.focus()
    }},
    { key: 'S', shift: true, description: 'Focus search field', handler: focusSearch },
    { key: 'K', shift: true, description: 'Show keyboard shortcuts', handler: () => setShortcutsOpen(true) },
    { key: '?', shift: true, description: 'Show keyboard shortcuts', handler: () => setShortcutsOpen(true) },
  ])

  return (
    <TooltipProvider delayDuration={300}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <div className="flex h-screen overflow-hidden bg-bg text-text">
        <Sidebar
          title={title}
          items={navItems}
          collapsed={collapsed}
          onToggle={toggleSidebar}
          navRef={navRef}
        />

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <header className="relative z-topbar flex h-topbar shrink-0 items-center justify-between gap-3 border-b border-border bg-surface px-3 sm:px-6">
            <div className="flex min-w-0 items-center gap-2">
              <Button
                variant="secondary"
                size="icon"
                className="md:hidden"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open navigation"
              >
                <Menu aria-hidden="true" />
              </Button>
              {/* Not a heading: each page renders its own single h1 (see PageHeader). */}
              <p className="truncate text-lg font-semibold text-text">{pageTitle}</p>
            </div>
            <div className="flex items-center gap-1">
              <ThemeToggle />
              <Button
                variant="ghost"
                size="icon"
                className="hidden sm:inline-flex"
                onClick={() => setShortcutsOpen(true)}
                title="Keyboard shortcuts (Shift+K)"
                aria-label="Keyboard shortcuts"
              >
                <Keyboard aria-hidden="true" />
              </Button>
              <ProfileMenu email={email} initials={initials} density={density} setDensity={setDensity} />
            </div>
          </header>

          <main id="main" ref={mainRef} tabIndex={-1} className="min-h-0 flex-1 overflow-hidden focus-visible:shadow-none">
            {children}
          </main>
        </div>

        <MobileNavigation items={navItems} open={mobileNavOpen} onOpenChange={setMobileNavOpen} />
        <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      </div>
    </TooltipProvider>
  )
}
